import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { BodyArea } from '@/api/treatmentLibrary/treatmentLibraryApi.types';
import { BODY_AREAS_QUERY_KEY } from '@/constants/queryKeys';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { useBodyAreasQuery } from '@/hooks/useTreatmentLibraryQueries';
import {
  useRemoveBodyAreaImageMutation,
  useReplaceBodyAreasMutation,
  useUploadBodyAreaImageMutation,
} from '@/hooks/useTreatmentLibraryMutations';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { EntityImageUpload } from './EntityImageUpload';

interface EditableArea {
  id: string | null;
  nameEn: string;
  namePl: string;
  treatmentCount: number;
  imageUrl: string | null;
  pendingImageFile: File | null;
  pendingImageRemoved: boolean;
}

function toRows(areas: BodyArea[]): EditableArea[] {
  return areas.map(function toRow(area) {
    return {
      id: area.id,
      nameEn: area.nameEn,
      namePl: area.namePl,
      treatmentCount: area.treatmentCount,
      imageUrl: area.imageUrl,
      pendingImageFile: null,
      pendingImageRemoved: false,
    };
  });
}

export function BodyAreasEditor() {
  const { t } = useTranslation('treatmentLibrary');
  const queryClient = useQueryClient();
  const areasQuery = useBodyAreasQuery(true);
  const mutation = useReplaceBodyAreasMutation();
  const uploadImage = useUploadBodyAreaImageMutation();
  const removeImage = useRemoveBodyAreaImageMutation();
  const [rows, setRows] = useState<EditableArea[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(
    function syncFromServer() {
      if (areasQuery.data !== undefined) {
        setRows(toRows(areasQuery.data));
      }
    },
    [areasQuery.data]
  );

  function updateRow(index: number, patch: Partial<EditableArea>) {
    setRows(function apply(previous) {
      return previous.map(function mapRow(row, rowIndex) {
        return rowIndex === index ? { ...row, ...patch } : row;
      });
    });
  }
  function removeRow(index: number) {
    setRows(function apply(previous) {
      return previous.filter(function keep(_row, rowIndex) {
        return rowIndex !== index;
      });
    });
  }
  function addRow() {
    setRows(function apply(previous) {
      return [
        ...previous,
        {
          id: null,
          nameEn: '',
          namePl: '',
          treatmentCount: 0,
          imageUrl: null,
          pendingImageFile: null,
          pendingImageRemoved: false,
        },
      ];
    });
  }
  function selectImage(index: number, file: File) {
    updateRow(index, {
      imageUrl: URL.createObjectURL(file),
      pendingImageFile: file,
      pendingImageRemoved: false,
    });
  }
  function clearImage(index: number) {
    updateRow(index, { imageUrl: null, pendingImageFile: null, pendingImageRemoved: true });
  }
  async function save() {
    const completeRows = rows.filter(function complete(row) {
      return row.nameEn.trim().length > 0 && row.namePl.trim().length > 0;
    });
    const drafts = completeRows.map(function toDraft(row) {
      return { id: row.id, nameEn: row.nameEn.trim(), namePl: row.namePl.trim() };
    });
    setIsSaving(true);
    try {
      const saved = await mutation.mutateAsync(drafts);
      let didChangeImage = false;
      for (let position = 0; position < completeRows.length; position += 1) {
        const row = completeRows[position];
        const savedArea = saved[position];
        if (row === undefined || savedArea === undefined) {
          continue;
        }
        if (row.pendingImageFile !== null) {
          await uploadImage.mutateAsync({ id: savedArea.id, file: row.pendingImageFile });
          didChangeImage = true;
        } else if (row.pendingImageRemoved && row.id !== null) {
          await removeImage.mutateAsync(savedArea.id);
          didChangeImage = true;
        }
      }
      if (didChangeImage) {
        await queryClient.invalidateQueries({ queryKey: BODY_AREAS_QUERY_KEY });
      }
      showSuccessNotification(t('bodyAreas.saved'));
    } catch (error) {
      showErrorNotification(resolveApiErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  }

  if (areasQuery.isLoading === true) {
    return (
      <div className="py-16 flex justify-center">
        <LoadingSpinner className="w-7 h-7" />
      </div>
    );
  }
  if (areasQuery.isError === true || areasQuery.data === undefined) {
    return (
      <QueryErrorState
        onRetry={function retry() {
          areasQuery.refetch();
        }}
      />
    );
  }

  return (
    <div>
      <p className="text-xs text-zoomuted flex items-center gap-2 mb-4">
        <Icon name="Info" />
        {t('bodyAreas.note')}
      </p>
      <Card>
        <div className="hidden sm:grid grid-cols-[auto_1fr_1fr_auto] gap-3 px-1 mb-2">
          <span className="w-11 text-[10px] font-bold uppercase tracking-wider text-zoomuted">
            {t('image.alt')}
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wider text-zoomuted">
            {t('field.english')}
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wider text-zoomuted">
            {t('field.polish')}
          </span>
          <span className="w-8" />
        </div>
        <div className="space-y-2.5">
          {rows.map(function renderRow(row, index) {
            return (
              <div
                key={index}
                className="flex flex-col sm:grid sm:grid-cols-[auto_1fr_1fr_auto] gap-2.5 sm:gap-3 sm:items-center border-b border-zooborder pb-3 last:border-b-0 last:pb-0 sm:border-b-0 sm:pb-0"
              >
                <EntityImageUpload
                  compact
                  imageUrl={row.imageUrl}
                  isBusy={isSaving}
                  onSelect={function pick(file) {
                    selectImage(index, file);
                  }}
                  onRemove={function drop() {
                    clearImage(index);
                  }}
                />
                <Input
                  value={row.nameEn}
                  placeholder={t('field.english')}
                  onChange={function update(changeEvent) {
                    updateRow(index, { nameEn: changeEvent.target.value });
                  }}
                />
                <Input
                  value={row.namePl}
                  placeholder={t('field.polish')}
                  onChange={function update(changeEvent) {
                    updateRow(index, { namePl: changeEvent.target.value });
                  }}
                />
                {row.treatmentCount > 0 ? (
                  <span
                    className="w-8 h-8 flex items-center justify-center text-zoodisabledtext"
                    title={t('bodyAreas.inUse', { count: row.treatmentCount })}
                  >
                    <Icon name="Lock" />
                  </span>
                ) : (
                  <Button
                    variant="destructiveGhost"
                    size="icon"
                    aria-label={t('bodyAreas.remove')}
                    onClick={function remove() {
                      removeRow(index);
                    }}
                    className="w-8 h-8"
                  >
                    <Icon name="Trash" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 pt-4 border-t border-zooborder">
          <Button
            type="button"
            variant="outline"
            size="small"
            className="w-full sm:w-auto whitespace-nowrap"
            disabled={isSaving}
            onClick={addRow}
          >
            <Icon name="Plus" /> {t('bodyAreas.add')}
          </Button>
          <Button
            type="button"
            className="w-full sm:w-auto whitespace-nowrap"
            isLoading={isSaving}
            onClick={save}
          >
            {t('bodyAreas.save')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
